import { Button, Menu, MenuItem, TextField } from '@material-ui/core';
import { OrderedMap } from 'immutable';
import * as React from 'react';

const white = '#ffffff';

import { timeMonthYear } from '../common/timestamp';
import { Client, Communication, Job, List } from '../state';

interface CommunicationWarningJobsProps {
    data: Communication;
    clients: List<Client>;
    jobs: OrderedMap<string, Job>;
    updateCommunicationMatchData: (
        communication: Communication,
        data: {
            ignored?: boolean;
            personIds?: string[];
            jobIds?: string[];
            error?: boolean;
            manualOverridePersonIds?: boolean;
            manualOverrideJobIds?: boolean;
            bug?: boolean;
        }
    ) => void;
}

export const CommunicationWarningJobs: React.FunctionComponent<CommunicationWarningJobsProps> = ({
    data,
    jobs,
    clients,
    updateCommunicationMatchData
}) => {
    const { personIds } = data;

    const computeError = () =>
        (!personIds || personIds.length !== 1) && !data.matchData.manualOverridePersonIds ? true : undefined;

    const handleSelectJob = (id: string) => {
        const error = computeError();
        updateCommunicationMatchData(data, { jobIds: [id], error, manualOverrideJobIds: true });
    };

    const markOk = () => {
        const error =
            (!personIds || personIds.length !== 1) && !data.matchData.manualOverridePersonIds ? true : undefined;
        updateCommunicationMatchData(data, { error, manualOverrideJobIds: true });
    };

    const { jobIds } = data;
    const [searchText, setSearchText] = React.useState('');
    const [jobMenuAnchor, setJobMenuAnchor] = React.useState<null | HTMLElement>(null);
    const [autoCompleteAnchor, setAutoCompleteAnchor] = React.useState<null | HTMLElement>(null);

    const handleOpenExistingJobsMenu = (event: React.MouseEvent<HTMLElement>) => {
        setJobMenuAnchor(event.currentTarget);
    };

    const handleCloseExistingJobsMenu = () => {
        setJobMenuAnchor(null);
    };

    const handleSelectExistingJob = (id: string) => () => {
        handleSelectJob(id);
        handleCloseExistingJobsMenu();
    };

    const handleAutoCompleteFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        setAutoCompleteAnchor(event.currentTarget);
    };

    const handleAutoCompleteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!autoCompleteAnchor) {
            setAutoCompleteAnchor(event.currentTarget);
        }
        setSearchText(event.target.value);
    };

    const handleCloseAutoComplete = () => {
        setAutoCompleteAnchor(null);
    };

    const handleSelectAutoCompleteOption = (option: { text: string; value: string }) => () => {
        handleSelectJob(option.value);
        setSearchText('');
        handleCloseAutoComplete();
    };

    let jobIdErrorMessage: string;
    let confirmButtonLabel: string;
    if (!jobIds || jobIds.length === 0) {
        jobIdErrorMessage = 'No job could be associated with this email';
        confirmButtonLabel = 'Confirm no job for email';
    } else if (jobIds.length > 1) {
        jobIdErrorMessage = 'Multiple jobs were associated with this email';
        confirmButtonLabel = `Confirm ${jobIds.length} jobs for email`;
    }
    const jobIdOptions = jobIds || jobs.keySeq().toArray();
    const jobOptions = jobIdOptions
        .map((jobId) => jobs.get(jobId))
        .sort((job1, job2) => {
            const job1Label = `${clients.list.get(job1.clientId).name} > ${job1.title} (${job1.createdAt})`;
            const job2Label = `${clients.list.get(job2.clientId).name} > ${job2.title} (${job2.createdAt})`;
            return job1Label.localeCompare(job2Label);
        });

    let jobSelector;
    if (jobIds && jobIds.length > 0) {
        const jobItems = jobOptions.map((job) => {
            const text = `${clients.list.get(job.clientId).name} > ${job.title} (${timeMonthYear(job.createdAt)})`;
            return (
                <MenuItem key={job.id} onClick={handleSelectExistingJob(job.id)}>
                    {text}
                </MenuItem>
            );
        });
        jobSelector = (
            <React.Fragment>
                <Button onClick={handleOpenExistingJobsMenu} style={{ color: white }} variant="text">
                    Assign Job
                </Button>
                <Menu
                    anchorEl={jobMenuAnchor}
                    keepMounted={true}
                    open={Boolean(jobMenuAnchor)}
                    onClose={handleCloseExistingJobsMenu}
                >
                    {jobItems}
                </Menu>
            </React.Fragment>
        );
    } else {
        const dataSource = jobOptions.map((j) => ({
            text: `${clients.list.get(j.clientId).name} > ${j.title} (${timeMonthYear(j.createdAt)})`,
            value: j.id
        }));
        const filteredOptions = dataSource.filter(({ text }) => text.toLowerCase().includes(searchText.toLowerCase()));
        const autoCompleteMenuItems = filteredOptions.map((option) => {
            const handleClick = handleSelectAutoCompleteOption(option);
            return (
                <MenuItem key={option.value} onClick={handleClick}>
                    {option.text}
                </MenuItem>
            );
        });
        jobSelector = (
            <React.Fragment>
                <TextField
                    className="email-header-warning-job-auto-complete"
                    value={searchText}
                    onFocus={handleAutoCompleteFocus}
                    onChange={handleAutoCompleteChange}
                    placeholder="Assign Job"
                    style={{ display: 'flex', flex: '1 1 auto', width: 'auto' }}
                    InputProps={{ disableUnderline: true, style: { color: white } }}
                />
                <Menu
                    anchorEl={autoCompleteAnchor}
                    keepMounted={true}
                    open={Boolean(autoCompleteAnchor) && filteredOptions.length > 0}
                    onClose={handleCloseAutoComplete}
                    PaperProps={{ style: { maxHeight: 400 } }}
                >
                    {autoCompleteMenuItems}
                </Menu>
            </React.Fragment>
        );
    }

    const shouldShowJobWarning = (!jobIds || jobIds.length !== 1) && !data.matchData.manualOverrideJobIds;
    const jobWarning = shouldShowJobWarning ? (
        <div className="email-header-warning email-job-warning">
            {jobIdErrorMessage}
            <div className="email-match-job-ids-selector">
                {jobSelector}
                <Button onClick={markOk} key="ok" variant="text" style={{ color: white }}>
                    {confirmButtonLabel}
                </Button>
            </div>
        </div>
    ) : null;

    return jobWarning;
};
